import { reactive, readonly, type Component } from "vue";

export type ConfirmVariant = "default" | "warning" | "destructive";

export interface ConfirmOptions {
  /** Title shown in the dialog header. */
  title: string;
  /** Plain-text body. Ignored when `body` is provided. */
  message?: string;
  /**
   * Optional inline emphasis. When set, the literal `XXXX` placeholder in
   * `message` is replaced by `highlight` rendered as a bold inline span.
   * No-op when `body` is provided or when `message` has no `XXXX`.
   */
  highlight?: string;
  /** Custom Vue component rendered in place of `message` (for rich bodies). */
  body?: Component;
  /** Semantic variant. Drives the warning icon and the confirm button color. */
  variant?: ConfirmVariant;
  /** Confirm button label. Defaults to t("button.confirm"). */
  confirmText?: string;
  /**
   * Cancel button label. Defaults to t("button.cancel").
   * Pass `null` to render a single confirm button
   */
  cancelText?: string | null;
}

interface ConfirmState {
  open: boolean;
  options: ConfirmOptions;
}

const DEFAULT_OPTIONS: ConfirmOptions = { title: "" };

const state = reactive<ConfirmState>({
  open: false,
  options: { ...DEFAULT_OPTIONS },
});

let resolver: ((value: boolean) => void) | null = null;

export const confirmDialogState = readonly(state);

/**
 * Show a confirmation dialog and await the user's decision.
 *
 * Resolves `true` on confirm, `false` on cancel / Esc / click-outside / dismiss.
 * Never rejects. If called while another confirm is in flight, the previous
 * promise resolves as `false` and the new dialog takes over.
 *
 * @param {ConfirmOptions} options - Title, body, variant and button labels.
 * @returns {Promise<boolean>} Resolves `true` on confirm, `false` otherwise.
 */
export function showConfirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (resolver) {
      resolver(false);
    }

    resolver = resolve;
    state.options = options;
    state.open = true;
  });
}

/**
 * Resolve the in-flight confirm with the given value and close the dialog.
 * Called by the ConfirmDialog component on button click or dismiss.
 *
 * @param {boolean} value - `true` for confirm, `false` for cancel / dismiss.
 * @returns {void}
 */
export function resolveConfirm(value: boolean): void {
  state.open = false;

  const pending = resolver;
  resolver = null;
  pending?.(value);
}
